import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Space, Input, Form, Typography, Tag, Switch, Row, Col, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, FolderOpenOutlined, RightOutlined } from '@ant-design/icons';
import { categoryService } from '../../services/categoryService';
import { useDispatch, useSelector } from 'react-redux';
import Paginations from '../../components/pagination';
import CategoryFormModal from '../../components/modals/categoryFormModal';
import { categoryActions } from '../../actions/categoryActions';
import FilterPopover from '../../components/filter';
import ConfirmModal from '../../components/confirmModal';
import TableData from '../../components/table';
import GuardedButton from '../../components/guardedButton';
import CategoryDetailPanel from './categoryDetailPanel';
import { useCurrentPageAccess } from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';
import { tenantActions } from '@/src/actions/tenantActions';

const { Text } = Typography;

interface QueryCategory {
  key: string;
  shortName?: string;
  _id?: string;
  categoryType?: string;
  name: string;
  isActive: boolean;
  categoryName?: string;
  description?: string;
  createdByName?: string;
  createdAt?: string;
  status: 'ENABLED' | 'DISABLED';
}

interface CategoryProps {
  role: string;
}


const Category: React.FC<CategoryProps> = ({ role }) => {

  const mountCount = useRef(0);
  const pageAccess = useCurrentPageAccess();
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filterData, setIsFilterData] = useState({ categoryType: "", isActive: '' });
  const dispatch = useDispatch();
  const [recordToDelete, setRecordToDelete] = useState<any>('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [mutableCategory, setMutableCategory] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>();
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const defaultPayLoad = { page: 1, limit: 10, };
  const [visible, setVisible] = useState(false);
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.limit));
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getCategory = useSelector((state: any) => state.getCategoryParamsReducer);
  const categoryData = useSelector((state: any) => state.createCategoryReducer);
  const deleteCategory = useSelector((state: any) => state.deleteCateoryReducer);
  const viewCategoryState = useSelector((state: any) => state.viewCategoryReducer);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    mountCount.current += 1;
    if (mountCount.current === 2 && currentPage === 1 && !debouncedSearchText?.trim()) {
      return;
    }
    const payload: any = {
      pageNumber: currentPage,
      pageSize,
    };

    if (debouncedSearchText?.trim()) {
      payload.searchText = debouncedSearchText.trim();
    }
    Object.entries(filterData).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        payload[k] = v;
      }
    });
    dispatch(categoryActions.getCategoryParams(payload));
  }, [currentPage, pageSize, debouncedSearchText, filterData, dispatch]);

  // Collapse any open detail panel whenever the result set changes underneath it.
  useEffect(() => {
    setExpandedKey(null);
    setIsEditMode(false);
  }, [currentPage, pageSize, debouncedSearchText, filterData]);

  useEffect(() => {
    if (categoryData && categoryData?.isSuccess) {
      setIsModalOpen(false);
      setIsEditMode(false);
      setSelectedCategory(null);
      dispatch(categoryActions.getCategoryParams({ pageNumber: currentPage, pageSize}));
      if (expandedKey) {
        dispatch(categoryActions.viewCategory(expandedKey));
      }
      categoryData.isSuccess = false;
    }
  }, [categoryData, dispatch, currentPage, pageSize, expandedKey]);

  useEffect(() => {
    if (getCategory && getCategory.isSuccess) {
      const category = getCategory?.categorydata?.categories ?? [];
      const mapped = Array.isArray(category) ? category.map((c: any) => ({
        ...c,
        createdAt: c?.createdAt,
        // Prefer a friendly name when available: createdByName, then createdByUser, then fallback to createdBy id/string
        createdBy: c?.createdByName || (c?.createdByUser ? `${c.createdByUser.firstName || ''}${c.createdByUser.lastName ? ' ' + c.createdByUser.lastName : ''}`.trim() : c?.createdBy),
      })) : [];
      setMutableCategory(mapped);
    }
    if (deleteCategory && deleteCategory.isSuccess) {
      dispatch(categoryActions.getCategoryParams({ pageNumber: currentPage, pageSize }));
      deleteCategory.isSuccess = false;
    }
  }, [getCategory, deleteCategory]);

  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
  };

  const onFinish = (values: any) => {
    dispatch(categoryActions.createCategory({
      _id: selectedCategory?._id,
      categoryName: values.categoryName,
      shortName: values.shortName,
      categoryType: values.categoryType,
    }));
  };


  const toggleStatus = async (cat: any, enabled: boolean) => {
    const id = cat._id ?? cat.key ?? cat.id;
    setMutableCategory(prev => prev.map((c: any) => {
      const cId = c._id ?? c.key ?? c.id;
      if (String(cId) === String(id)) {
        return {
          ...c,
          isActive: !!enabled,
          status: enabled ? 'ENABLED' : 'DISABLED',
          loading: true,
        };
      }
      return c;
    }));
    try {
      await categoryService.toggleCategory({
        _id: id,
        categoryName: cat.categoryName ?? cat.name,
        shortName: cat.shortName,
        categoryType: cat.categoryType,
        isActive: enabled
      });
      setMutableCategory(prev => prev.map((c: any) => {
        const cId = c._id ?? c.key ?? c.id;
        if (String(cId) === String(id)) {
          const copy = { ...c };
          delete copy.loading;
          return copy;
        }
        return c;
      }));
    } catch (err: any) {
      setMutableCategory(prev => prev.map((c: any) => {
        const cId = c._id ?? c.key ?? c.id;
        if (String(cId) === String(id)) {
          return {
            ...c,
            isActive: !enabled,
            status: !enabled ? 'ENABLED' : 'DISABLED',
            loading: false,
          };
        }
        return c;
      }));

    }
  };

  const handleApplyFilter = (values: any) => {

    const nextPage = 1;
    setCurrentPage(nextPage);
    const newFilterData: any = { categoryType: "", isActive: '' };
    const appliedFiltersData: any = {};
    if (values?.categoryType) {
      newFilterData.categoryType = values.categoryType;
      appliedFiltersData.categoryType = values.categoryType;
    }
    if (values?.status !== undefined && values?.status !== null) {
      newFilterData.isActive = values.status;
      appliedFiltersData.isActive = values.status;
    }
    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersData);
  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilterData({ categoryType: "", isActive: '' });
    setAppliedFilters({});
  };

  const handleDelete = () => {
    dispatch(categoryActions.deleteCategory(recordToDelete));
    setVisible(false);
    setExpandedKey(null);
    setIsEditMode(false);
  };

  // ── Inline master-detail handlers ─────────────────────────────
  const handleExpand = (expanded: boolean, record: any) => {
    const key = String(record._id);
    setIsEditMode(false);
    editForm.resetFields();
    if (expanded) {
      setExpandedKey(key);
      dispatch(categoryActions.viewCategory(record._id));
    } else {
      setExpandedKey(null);
    }
  };

  const handlePanelEdit = (detail: any) => {
    editForm.setFieldsValue({
      categoryName: detail?.categoryName,
      shortName: detail?.shortName,
      categoryType: detail?.categoryType,
    });
    setIsEditMode(true);
  };

  const handlePanelCancel = () => {
    setIsEditMode(false);
    editForm.resetFields();
  };

  const handlePanelSave = (detail: any) => {
    editForm.validateFields().then((values) => {
      dispatch(categoryActions.createCategory({
        _id: detail?._id,
        categoryName: values.categoryName,
        shortName: values.shortName,
        categoryType: values.categoryType,
      }));
    });
  };

  
  const columns = [
    {
      title: 'Name',
      dataIndex: 'categoryName',
      key: 'name',
      ellipsis: {
      showTitle: false,
    },
      render: (text: string) => (
      <Tooltip title={text}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          <FolderOpenOutlined style={{ color: '#4f46e5', flexShrink: 0 }} />
          <Text strong ellipsis={{ tooltip: text }} style={{ maxWidth: '100%' }}>{text}</Text>
        </div>
      </Tooltip>
      ),
    },
    {
      title: 'Short Name',
      dataIndex: 'shortName',
      key: 'shortName',
      ellipsis: {
      showTitle: false,
    },
      render: (d: string) => <Tooltip title={d || '-'}><Text type="secondary">{d ? d : "-"}</Text> </Tooltip>,
    },
    {
      title: 'Category Type',
      dataIndex: 'categoryType',
      key: 'categoryType',
      ellipsis: {
      showTitle: false,
    },
      render: (d: string) => <Tooltip title={d || '-'}><Text type="secondary">{d ? d : "-"}</Text></Tooltip>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_: any, record: QueryCategory) => (
        <Space>
          <Tag color={record.isActive ? 'green' : 'default'} style={{ border: "1px solid" }}>{record.isActive ? 'Enabled' : 'Disabled'}</Tag>
          <span onClick={(e) => e.stopPropagation()}>
            <Switch className="category-status-switch" checked={record.isActive} loading={Boolean((record as any).loading)} onChange={(checked) => toggleStatus(record, checked)} />
          </span>
        </Space>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: any, record: QueryCategory) => (
        <Space orientation="vertical" size={0}>
          {record.createdAt ? (
            <Text type="secondary" className="text-small">
              {new Date(record.createdAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
            </Text>
          ) : (
            <Text type="secondary" className="text-small">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: {
      showTitle: false,
    },
      render: (_: any, record: QueryCategory) => (     <Tooltip title={record.createdByName || '-'}>
        <Text ellipsis={{ tooltip: record.createdByName }}>{record.createdByName ?? '-'}</Text></Tooltip>
      ),
    },
  ];

  const filterFields = useMemo(() => {
    return [
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Select Status',
        options: [
          { label: 'Active', value: true },
          { label: 'Inactive', value: false },
        ],
      },
      {
        name: 'categoryType',
        label: 'Category Type',
        type: 'select',
        placeholder: 'Select Category Type',
        options: [
          { label: 'Service', value: 'SERVICE' },
          { label: 'Query', value: 'QUERY' },
        ],
      },
    ];
  }, []);

  const canEdit = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;

  // Merge full detail (audit fields) from the view endpoint over the live list row,
  // letting the live row win for volatile fields like isActive.
  const viewData = (viewCategoryState?.isSuccess && String(viewCategoryState?.categoryData?._id) === String(expandedKey))
    ? viewCategoryState.categoryData
    : null;
  const detailLoading = Boolean(viewCategoryState?.isLoading) && !viewData;

  const renderExpanded = (record: any) => {
    // Keep the view endpoint's audit objects (createdBy/updatedBy carry the user's name);
    // the list row only has their ids, so it must not override them.
    const detail = viewData
      ? { ...viewData, ...record, createdBy: viewData.createdBy ?? record.createdBy, updatedBy: viewData.updatedBy ?? record.updatedBy }
      : record;

    return (
      <CategoryDetailPanel
        detail={detail}
        loading={detailLoading}
        isEditing={isEditMode}
        canEdit={canEdit}
        role={role as string}
        saving={Boolean(categoryData?.isLoading)}
        statusLoading={Boolean(record?.loading)}
        form={editForm}
        onEdit={() => handlePanelEdit(detail)}
        onCancel={handlePanelCancel}
        onSave={() => handlePanelSave(detail)}
        onDelete={() => { setRecordToDelete(detail?._id); setVisible(true); }}
        onToggleStatus={(checked) => toggleStatus(record, checked)}
      />
    );
  };

   useEffect(() => {
      getCategory.isSuccess = false;
    }, []);

  useEffect(() => {
    if (getCategory.isSuccess || getCategory.isError) {
      setInitialLoaded(true);
    }
  }, [getCategory.isSuccess, getCategory.isError]);

  if (!initialLoaded && getCategory.isLoading) {
    return <SpinnerLoader />;
  }

  return (
    <div className="category-page">
      <Row justify="space-between" align="middle" className="mb-4 category-header">
        <div className="category-toolbar">
          <Input className='category-search-text' placeholder="Search by name or description" prefix={<SearchOutlined />}
            allowClear value={searchText} onChange={(e) => setSearchText(e.target.value)}
          />
          <FilterPopover appliedCount={Object.keys(appliedFilters).length} fields={filterFields}
            onApply={handleApplyFilter} onClear={handleClearFilter} />
        </div>
        {(role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isAdd && <Col className='button-col'> <GuardedButton type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>Add Category</GuardedButton> </Col>}
      </Row >
        {getCategory?.isLoading && <SpinnerLoader />}
      <div className="driveMynt-card">
        <TableData
          className='category-table category-table--expandable'
          columns={columns}
          rowKey={(record: any) => String(record._id)}
          dataSource={mutableCategory}
          pagination={false}
          expandable={{
            expandedRowKeys: expandedKey ? [expandedKey] : [],
            onExpand: handleExpand,
            expandedRowRender: renderExpanded,
            expandRowByClick: pageAccess.isView,
            rowExpandable: () => pageAccess.isView,
            showExpandColumn: pageAccess.isView,
            columnWidth: 48,
            expandIcon: ({ expanded, onExpand, record }: any) => (
              <span
                className={`cat-expand-ic${expanded ? ' cat-expand-ic--open' : ''}`}
                onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
              >
                <RightOutlined />
              </span>
            ),
          }}
          onRow={(record: any) => ({
            className: String(record._id) === String(expandedKey) ? 'cat-row-active' : '',
            style: { cursor: pageAccess.isView ? 'pointer' : 'default' },
          })}
        />
        <Paginations total={getCategory?.categorydata?.pagination?.totalRecords} defaultCurrent={currentPage} defaultPageSize={pageSize}
          callback={handleOnPageChange} />
      </div>
      <CategoryFormModal open={isModalOpen} title={editing ? 'Edit Category' : 'Add New Category'} onCancel={() => { setIsModalOpen(false); form.resetFields(); }}
        onFinish={onFinish} form={form} isLoading={Boolean(categoryData?.isLoading)} initialValues={editing} />

      <ConfirmModal centered content="Are you sure you want to delete this category?" visible={visible}
        cancelText='cancel' okText='ok' title='Confirm Delete' onOk={handleDelete} onCancel={() => setVisible(false)} />


    </div>
  );
};
export default Category;
